"use client";

import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setPreviewHiddenSectionIds,
  setSections,
} from "@/store/slices/webbuilderSlice";
import { CanvasContent } from "@/components/webbuilder/features/canvas/CanvasContent";
import { usePages } from "@/hooks/usePages";
import { WebBuilderPreloader } from "@/components/webbuilder/ui/loaders/WebBuilderPreloader";
import {
  createErrorMessage,
  createReadyMessage,
  createSelectSectionMessage,
  isPreviewProtocolMessage,
  isSnapshotMessage,
  type PreviewPageMetadata,
  type WebbuilderViewportMode,
} from "@/components/webbuilder/features/preview/previewProtocol";
import { setCurrentPageForPreview } from "@/store/slices/page.slice";
import type { Page, Section } from "@/types/models/page";

const buildPreviewPage = (
  metadata: Partial<PreviewPageMetadata> & { id: string },
  sections: Section[],
): Page => ({
  id: metadata.id,
  name: metadata.name || "Preview",
  slug: metadata.slug || "preview",
  status: metadata.status || "draft",
  store_id: metadata.store_id || "",
  template_id: metadata.template_id || "",
  title: metadata.title || metadata.name || "Preview",
  description: metadata.description || "",
  sections,
  created_at: "",
  updated_at: "",
  featured_image_asset_id: null,
  preview_image_asset_id: null,
});

export default function PreviewPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const draftId = searchParams.get("draftId");
  const dispatch = useAppDispatch();
  const { actions } = usePages();
  const currentPage = useAppSelector(
    (state) => state.page?.currentPage || null,
  );
  const currentPageRef = useRef<Page | null>(currentPage);
  const [viewportMode, setViewportMode] =
    useState<WebbuilderViewportMode>("desktop");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const postToParent = (message: unknown) => {
    if (typeof window === "undefined" || window.parent === window) return;
    window.parent.postMessage(message, window.location.origin);
  };

  const handlePreviewSectionSelect = (section: Section) => {
    postToParent(createSelectSectionMessage(section.id));
  };

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    let active = true;

    const loadDraft = async () => {
      if (!draftId) {
        if (active) {
          setError("Missing draftId in preview URL.");
          setLoading(false);
        }
        return;
      }

      if (!pageId) {
        if (active) {
          setError("Missing pageId in preview URL.");
          setLoading(false);
        }
        return;
      }

      try {
        const response = await actions.getPageDraft(pageId, draftId);
        const draftResponse = response?.data;
        const sections = draftResponse?.sections || [];
        const fallbackPage =
          currentPageRef.current?.id === pageId ? currentPageRef.current : null;
        const page = buildPreviewPage(
          (draftResponse as any)?.page || fallbackPage || { id: pageId },
          sections,
        );
        dispatch(setSections(sections));
        dispatch(setCurrentPageForPreview({ page }));
        postToParent(createReadyMessage(draftId));
      } catch (caughtError) {
        if (active) {
          const message =
            caughtError instanceof Error
              ? caughtError.message
              : "Failed to load draft preview.";
          setError(message);
          postToParent(createErrorMessage(message));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDraft();

    return () => {
      active = false;
    };
  }, [actions, dispatch, draftId, pageId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== window.parent) return;
      if (!isPreviewProtocolMessage(event.data)) return;
      if (!isSnapshotMessage(event.data)) return;

      const { snapshot } = event.data;
      const page = buildPreviewPage(snapshot.page, snapshot.sections);
      setViewportMode(snapshot.viewportMode);
      dispatch(setSections(snapshot.sections));
      dispatch(setPreviewHiddenSectionIds(snapshot.previewHiddenSectionIds));
      dispatch(setCurrentPageForPreview({ page }));
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [dispatch]);

  useEffect(() => {
    const showBlockedNotice = () => {
      setNotice("Navigation is disabled in preview.");
      window.setTimeout(() => setNotice(null), 2500);
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const isSamePageHash = href.startsWith("#");
      if (isSamePageHash) return;

      event.preventDefault();
      event.stopPropagation();
      showBlockedNotice();
    };

    const handleSubmit = (event: SubmitEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setNotice("Form submission is disabled in preview.");
      window.setTimeout(() => setNotice(null), 2500);
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("submit", handleSubmit, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("submit", handleSubmit, true);
    };
  }, []);

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-2xl border border-border bg-white p-6 shadow-xl dark:bg-auth-card">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Preview unavailable</h1>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <WebBuilderPreloader message="Loading draft preview..." />
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-background">
      {notice && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-full border border-border bg-white/95 px-4 py-2 text-sm text-foreground shadow-lg backdrop-blur dark:bg-auth-card/95">
          {notice}
        </div>
      )}

      <div className="h-full w-full overflow-auto">
        <CanvasContent
          currentPage={currentPage}
          storeId={storeId || ""}
          viewportMode={viewportMode}
          showEditorControls={false}
          onPreviewSectionSelect={handlePreviewSectionSelect}
        />
      </div>
    </div>
  );
}
